/**
 * ArtifactCapabilityEditor
 * 
 * Maps training artifacts to capability keys with weight and primary flag.
 * Embedded inside EditTrainingArtifactDialog as a collapsible section.
 */

import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Plus, Trash2, Loader2, Star } from 'lucide-react';
import { toast } from 'sonner';
import {
  useTrainingCapabilities,
  useArtifactCapabilityMappings,
  useUpsertCapabilityMapping,
  useDeleteCapabilityMapping,
} from '@/hooks/useTrainingCapabilities';
import {
  groupCapabilitiesByCluster,
  formatClusterLabel,
  clampWeight,
  ROLE_TARGET_LABELS,
  type CapabilityKey,
  type CapabilityCluster,
  type TrainingCapability,
} from '@/constants/trainingCapabilities';

const MAX_PRIMARY = 3;

interface ArtifactCapabilityEditorProps {
  artifactId: string;
}

export function ArtifactCapabilityEditor({ artifactId }: ArtifactCapabilityEditorProps) {
  const { data: capabilities, isLoading: capsLoading } = useTrainingCapabilities();
  const { data: mappings, isLoading: mapsLoading } = useArtifactCapabilityMappings(artifactId);
  const upsertMapping = useUpsertCapabilityMapping();
  const deleteMapping = useDeleteCapabilityMapping();
  const [selectedKey, setSelectedKey] = useState<string>('');

  const isLoading = capsLoading || mapsLoading;
  const mappedKeys = new Set(mappings?.map(m => m.capability_key) || []);
  const primaryCount = mappings?.filter(m => m.is_primary).length || 0;

  const grouped = capabilities ? groupCapabilitiesByCluster(capabilities) : {};
  const availableGrouped = capabilities
    ? groupCapabilitiesByCluster(capabilities.filter(c => !mappedKeys.has(c.key)))
    : {};

  const handleAdd = () => {
    if (!selectedKey) return;
    upsertMapping.mutate(
      { artifactId, capabilityKey: selectedKey as CapabilityKey },
      { onSuccess: () => setSelectedKey('') }
    );
  };

  const handleWeightChange = (mappingKey: CapabilityKey, weight: number) => {
    upsertMapping.mutate({ artifactId, capabilityKey: mappingKey, weight: clampWeight(weight) });
  };

  const handlePrimaryToggle = (mappingKey: CapabilityKey, currentlyPrimary: boolean) => {
    if (!currentlyPrimary && primaryCount >= MAX_PRIMARY) {
      toast.warning(`Max ${MAX_PRIMARY} primary capabilities per artifact`);
      return;
    }
    upsertMapping.mutate({ artifactId, capabilityKey: mappingKey, isPrimary: !currentlyPrimary });
  };

  const handleRemove = (mappingId: string) => {
    deleteMapping.mutate({ mappingId, artifactId });
  };

  const getCapLabel = (key: string): string => {
    return capabilities?.find(c => c.key === key)?.label || key;
  };

  const getCapability = (key: string): TrainingCapability | undefined => {
    return capabilities?.find(c => c.key === key);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-6">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs text-muted-foreground">
            Capabilities represent <span className="font-medium">skill uplift</span>; Domains represent system/topic.
          </p>
        </div>
        <Badge variant="outline" className="text-xs shrink-0">
          {mappings?.length || 0} mapped
        </Badge>
      </div>

      {/* Add capability */}
      <div className="flex gap-2">
        <Select value={selectedKey} onValueChange={setSelectedKey}>
          <SelectTrigger className="flex-1 h-8 text-xs">
            <SelectValue placeholder="Add capability..." />
          </SelectTrigger>
          <SelectContent className="max-h-[300px]">
            {(Object.entries(availableGrouped) as [CapabilityCluster, TrainingCapability[]][]).map(
              ([cluster, caps]) =>
                caps.length > 0 && (
                  <SelectGroup key={cluster}>
                    <SelectLabel className="text-xs font-semibold text-muted-foreground">
                      {formatClusterLabel(cluster)}
                    </SelectLabel>
                    {caps.map(cap => (
                      <SelectItem key={cap.key} value={cap.key} className="text-xs">
                        <span className="flex items-center gap-1.5">
                          {cap.label}
                          {cap.role_targets.map(r => (
                            <Badge key={r} variant="outline" className="text-[10px] px-1 py-0 h-4">
                              {ROLE_TARGET_LABELS[r] || r}
                            </Badge>
                          ))}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectGroup>
                )
            )}
          </SelectContent>
        </Select>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-8"
          onClick={handleAdd}
          disabled={!selectedKey || upsertMapping.isPending}
        >
          <Plus className="h-3 w-3" />
        </Button>
      </div>

      {/* Mapped capabilities grouped by cluster */}
      {mappings && mappings.length > 0 ? (
        <div className="space-y-3">
          {(Object.entries(grouped) as [CapabilityCluster, TrainingCapability[]][]).map(
            ([cluster, caps]) => {
              const clusterMappings = mappings.filter(m =>
                caps.some(c => c.key === m.capability_key)
              );
              if (clusterMappings.length === 0) return null;
              return (
                <div key={cluster} className="space-y-1.5">
                  <p className="text-xs font-medium text-muted-foreground">
                    {formatClusterLabel(cluster)}
                  </p>
                  {clusterMappings.map(mapping => {
                    const cap = getCapability(mapping.capability_key);
                    return (
                      <div
                        key={mapping.id}
                        className="flex items-center gap-2 rounded-md border border-border p-2 text-xs"
                      >
                        <div className="flex items-center gap-1 min-w-0 flex-1">
                          <button
                            type="button"
                            onClick={() =>
                              handlePrimaryToggle(mapping.capability_key as CapabilityKey, mapping.is_primary)
                            }
                            className="shrink-0"
                            title={mapping.is_primary ? 'Primary capability' : 'Mark as primary'}
                          >
                            <Star
                              className={`h-3.5 w-3.5 ${
                                mapping.is_primary
                                  ? 'fill-amber-400 text-amber-400'
                                  : 'text-muted-foreground/40 hover:text-muted-foreground'
                              }`}
                            />
                          </button>
                          <span className="truncate font-medium">{getCapLabel(mapping.capability_key)}</span>
                          {cap?.role_targets.map(r => (
                            <Badge key={r} variant="outline" className="text-[10px] px-1 py-0 h-4 shrink-0">
                              {ROLE_TARGET_LABELS[r] || r}
                            </Badge>
                          ))}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="text-muted-foreground w-6 text-right">{mapping.weight}</span>
                          <Slider
                            min={1}
                            max={30}
                            step={1}
                            value={[Math.round(mapping.weight * 10)]}
                            onValueCommit={([v]) =>
                              handleWeightChange(mapping.capability_key as CapabilityKey, v / 10)
                            }
                            className="w-20"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={() => handleRemove(mapping.id)}
                          >
                            <Trash2 className="h-3 w-3 text-muted-foreground hover:text-destructive" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            }
          )}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground text-center py-4">
          No capabilities mapped yet. Add one above.
        </p>
      )}
    </div>
  );
}
